import React, { useState } from 'react';
import { createOrUpdateResidentialUnitGroup, getResidentialUnitGroup } from '@/services/residential-unit-groups';
import { Modal, ModalHeader, ModalBody, ModalFooter, LoadingIndicator, Select, SelectOption } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationForm, ValidatingInput, ValidationSummary } from '@/components/forms/validation';
import { Button } from '@/components/Buttons';
import { ResidentialUnitGroup, OptionalId } from '@/types';
import { useMergeState } from '@/utils/hooks';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useConstructionPhases } from '@/services/construction-phase';

interface ResidentialUnitGroupEditDialogProps {
    projectId: number;
    residentialUnitGroupId?: number;
    show: boolean;
    onClose(): void;
    // onCancel?(): void;
    onSave(updatedGroup: ResidentialUnitGroup): void;
}

const ResidentialUnitGroupEditDialog: React.FC<ResidentialUnitGroupEditDialogProps> = ({
    projectId,
    residentialUnitGroupId,
    // onCancel,
    onClose,
    onSave,
    show,
}) => {
    const [saving, setSaving] = useState(false);
    const { data: constructionPhases } = useConstructionPhases(projectId);
    const [residentialUnitGroup, mergeResidentialUnitGroup, setResidentialUnitGroup] =
        useMergeState<OptionalId<ResidentialUnitGroup> | null>(null);

    const handleOpen = async () => {
        setResidentialUnitGroup(null);

        if (residentialUnitGroupId) {
            setResidentialUnitGroup(await getResidentialUnitGroup(residentialUnitGroupId));
        } else {
            setResidentialUnitGroup({ name: '', projectId, constructionPhaseId: null });
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAcceptClick = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const result = await createOrUpdateResidentialUnitGroup(residentialUnitGroup!);
            triggerHandler(onSave, result);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const constructionPhasesOptions: SelectOption[] | undefined = constructionPhases?.map((cp) => ({
        label: cp.name,
        value: cp.id,
    }));
    if (constructionPhasesOptions) {
        constructionPhasesOptions.unshift({
            label: <Trans>None</Trans>,
            value: null,
        });
    }

    const renderContent = () => {
        if (!residentialUnitGroup || !constructionPhases) {
            return <LoadingIndicator />;
        }

        return (
            <div className="form labels-30">
                <div className="form-group row">
                    <label>
                        <Trans>Name</Trans>
                        <ValidatingInput
                            required={true}
                            type="text"
                            className="form-control"
                            placeholder={t`Name`}
                            value={residentialUnitGroup.name}
                            onChange={(e) => mergeResidentialUnitGroup({ name: e.target.value })}
                        />
                    </label>
                </div>
                <div className="form-group row">
                    {constructionPhasesOptions && (
                        <label>
                            <Trans>Construction phase</Trans>
                            <Select
                                className="form-control-unstyled"
                                options={constructionPhasesOptions}
                                defaultValue={constructionPhasesOptions[0]}
                                onChange={(v: any) => mergeResidentialUnitGroup({ constructionPhaseId: v.value })}
                                value={constructionPhasesOptions.find(
                                    (cp) => cp.value === residentialUnitGroup.constructionPhaseId
                                )}
                            />
                        </label>
                    )}
                </div>
                <div className="label-offset">
                    <ValidationSummary />
                </div>
            </div>
        );
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen}>
            <ValidationForm onSubmit={handleAcceptClick}>
                <ModalHeader>
                    {residentialUnitGroupId ? (
                        <Trans>Edit residential unit group</Trans>
                    ) : (
                        <Trans>Create new unit group</Trans>
                    )}
                </ModalHeader>
                <ModalBody>{renderContent()}</ModalBody>
                <ModalFooter>
                    <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button type="submit" color="primary" loading={saving} disabled={!residentialUnitGroup}>
                        {residentialUnitGroupId ? <Trans>Update</Trans> : <Trans>Create</Trans>}
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default ResidentialUnitGroupEditDialog;
